import {
  Badge,
  Button,
  FlexBox,
  Icon,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { sizes, typography } from '@dixa/lib-shared-tokens';
import { ampli } from 'lib-shared-tracking';
import { AsyncStatus } from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { MouseEventHandler, useCallback } from 'react';

import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

type ConversationsBulkToolbarProps = {
  /** The location where the toolbar is used. */
  location: 'conversations' | 'search';
  /** The async status. */
  asyncStatus: AsyncStatus;
  /** The conversations ids to perform bulk actions on. */
  selectedConversationIds: readonly number[];
  /** The ids of the eligible conversation for bulk actions. */
  eligibleBulkConversationIds: readonly number[];
  /** The total amount of filtered conversations. */
  totalFilteredConversations: number;
  /** The callback for selecting all eligible conversations for bulk actions. */
  onSelectAll: () => void;
  /** The callback for clearing selected conversations. */
  onClearAll?: () => void;
} & PropsWithClassName;

/** Represents the conversations bulk toolbar component. */
// TODO: [TEAMS-8303] handle potential rejected async status
export const ConversationsBulkToolbar = ({
  className,
  location,
  asyncStatus,
  selectedConversationIds,
  eligibleBulkConversationIds,
  totalFilteredConversations,
  onSelectAll,
  onClearAll,
}: ConversationsBulkToolbarProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const isBulkUpdateDisabled =
    asyncStatus === 'rejected' ||
    selectedConversationIds.length > eligibleBulkConversationIds.length ||
    selectedConversationIds.length === 0;

  /** Handles clearing all conversations. */
  const handleClearAll = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(() => {
    onClearAll?.();

    ampli.conversationsClearAllClicked();
  }, [onClearAll]);

  /** Handles selecting all eligible conversations. */
  const handleSelectAll = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(() => {
    onSelectAll();

    if (location === 'conversations') {
      ampli.conversationsSelectAllClicked();
    }
  }, [location, onSelectAll]);

  /** Handles the bulk update. */
  const handleBulkUpdate = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(async () => {
    ampli.conversationsBulkActionsClicked({
      location,
      modalVersion: '2',
    });

    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:conversationsBulk',
          arg: {
            conversationIds: selectedConversationIds,
            location: 'conversations',
          },
        },
      }),
    );

    if (isConfirmed) {
      onClearAll?.();
    }
  }, [dispatch, location, onClearAll, selectedConversationIds]);

  const isSelectionOverTheLimit =
    totalFilteredConversations > eligibleBulkConversationIds.length;
  const label = isSelectionOverTheLimit
    ? `Select first ${eligibleBulkConversationIds.length}`
    : `Select all ${eligibleBulkConversationIds.length}`;
  const tooltip = isSelectionOverTheLimit
    ? `Selects first ${eligibleBulkConversationIds.length} filtered conversations for bulk update`
    : 'Selects all filtered conversations for bulk update';

  return (
    <FlexBox
      className={className}
      alignItems="center"
      gap={sizes.spacer['200']}
    >
      {(asyncStatus === 'pending' || asyncStatus === 'uninitialized') && (
        <Spinner size={16} />
      )}

      {asyncStatus === 'fulfilled' &&
        eligibleBulkConversationIds.length > 0 && (
          <Tooltip content={tooltip}>
            <Button variant="tertiary" onClick={handleSelectAll}>
              {label}
            </Button>
          </Tooltip>
        )}

      {asyncStatus === 'rejected' && (
        <Tooltip
          content="Something went wrong, try clearing the selection and start again."
          wrap="div"
          wrapProps={{
            style: {
              display: 'flex',
              color: typography.color.error,
            },
          }}
        >
          <Icon size={16} name="exclamation" />
        </Tooltip>
      )}

      {onClearAll && (
        <Button variant="tertiary" onClick={handleClearAll}>
          Clear all
        </Button>
      )}

      <Button
        variant="primary"
        disabled={isBulkUpdateDisabled}
        slotEnd={
          <Badge variant="secondary" size="small">
            {selectedConversationIds.length}
          </Badge>
        }
        onClick={handleBulkUpdate}
      >
        Bulk update
      </Button>
    </FlexBox>
  );
};
